import { useCallback, useEffect, useState } from 'react'
import { Document, Word } from '../hooks/useDoc'
import Controls from './Controls'
import Token from './Token'
import { Vocab } from 'src/hooks/useVocab'
import Tag from './Tag'

type PageProps = {
  document?: Document
  words?: Word[]
  vocab?: Vocab[]
  pageNum?: number
  characterNum?: number
  translation?: string
  fromLang: string
  toLang: string
  numVocab: number
  lang: string
  isLoading: boolean
  onPageNumChange: (pageNum: number) => any
  onWordClick: (event: any, word: Word) => any
  onVocabCheck: (
    event: any,
    word: Word,
    vocab?: Vocab[],
  ) => any
  onFlip: () => any
  onOptions: () => any
  onVocab: () => any
}

const Page = ({
  document,
  words,
  translation,
  pageNum,
  characterNum,
  numVocab,
  vocab,
  fromLang,
  toLang,
  lang,
  onPageNumChange,
  onWordClick,
  onFlip,
  onOptions,
  onVocabCheck,
  onVocab,
  isLoading,
}: PageProps) => {
  const [localPageNum, setLocalPageNum] = useState<string>()

  useEffect(() => {
    if (pageNum === undefined) return

    setLocalPageNum(pageNum.toString())
  }, [pageNum])

  const isTranslation = lang !== fromLang

  const handleBlur = useCallback(
    (p?: string) => {
      if (!p) {
        if (pageNum === undefined) return

        setLocalPageNum(pageNum.toString())

        return
      }

      onPageNumChange(+p - (pageNum || 0))
    },
    [onPageNumChange, pageNum],
  )

  return (
    <div className="flex h-full flex-col justify-between bg-white px-12 pt-8">
      <div className="relative h-full max-h-[90%] overflow-hidden">
        {!document && !isLoading && (
          <div className="w-full text-center">
            Failed to load document
          </div>
        )}
        {!document && isLoading && (
          <div className="mt-2 mb-8 flex animate-pulse">
            <div className="flex-1 space-y-6 py-1">
              <div className="h-2 rounded bg-slate-200"></div>
              <div className="h-2 w-1/2 rounded bg-slate-200"></div>
            </div>
          </div>
        )}
        {document && (
          <h1 className="relative mx-[2px] mb-4 flex w-[95%] justify-between pt-2 text-lg font-extrabold">
            <div className="overflow-hidden text-ellipsis whitespace-nowrap">
              {document.title}
            </div>
            <span
              className={`east-out transition hover:cursor-pointer hover:opacity-50 ${
                !!isTranslation && 'opacity-20'
              }`}
            >
              <div>
                <input
                  onChange={(e) =>
                    setLocalPageNum(e.target.value)
                  }
                  onBlur={() => handleBlur(localPageNum)}
                  className="w-16 rounded-sm border-solid border-black px-2 text-right"
                  value={
                    (localPageNum === undefined
                      ? pageNum
                      : localPageNum) || 1
                  }
                />
              </div>
            </span>
          </h1>
        )}
        {isLoading && (
          <div className="mt-6 max-h-[90%] w-full rounded-lg bg-white">
            <div className="flex animate-pulse ">
              <div className="flex-1 space-y-6 py-1">
                <div className="h-2 rounded bg-slate-200 "></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 h-2 rounded bg-slate-200 "></div>
                  <div className="col-span-1 h-2 rounded bg-slate-200 "></div>
                </div>
                <div className="h-2 rounded bg-slate-200 "></div>
              </div>
            </div>
          </div>
        )}
        {translation && (
          <div className="absolute top-20 z-30 h-4 w-full bg-gradient-to-b from-white to-transparent">
            {isTranslation && (
              <Tag
                style={{
                  color: 'black',
                  position: 'absolute',
                  backgroundColor: 'rgb(143, 231, 138)',
                  transition: 'all 0.2s ease-in-out',
                  left: '50%',
                  transform: 'translate(-50%, -1.5rem)',
                }}
              >
                <p>Translation View</p>
              </Tag>
            )}
          </div>
        )}
        {translation && (
          <p
            className={`absolute top-20 left-0 flex h-[calc(100%-4rem)] w-full flex-col gap-4 overflow-y-auto leading-[2rem] ${
              isTranslation
                ? 'select-none opacity-[0.6]'
                : 'pointer-events-none opacity-0'
            }`}
          >
            {translation
              .split('\n')
              .filter((l) => !!l)
              .map((line, index) => (
                <span key={index}>
                  {index !== 0 && <>&ensp;&ensp;</>}
                  {line}
                </span>
              ))}
          </p>
        )}
        {translation && (
          <div className="absolute bottom-0 z-30 h-4 w-full bg-gradient-to-t from-white to-transparent"></div>
        )}
        {!isLoading && words && !isTranslation && (
          <p className="h-[calc(100%-4rem) relative leading-8">
            {words.map((word: Word, index: number) => {
              const matchingVocab = vocab?.find(
                (v) =>
                  characterNum &&
                  v.characterNum &&
                  // characterNum > v.characterNum &&
                  v.word.token === word.token,
              )

              return (
                <>
                  {/* Spacer. Allows word-wrapping */}
                  {index !== 0 && (
                    <span
                      className="display-none select-none"
                      key={`spacer-${index}`}
                    >
                      {' '}
                    </span>
                  )}
                  {!matchingVocab && (
                    <Token
                      onClick={(event) =>
                        onWordClick(event, word)
                      }
                      key={`${word.token}-${index}`}
                    >
                      {word.token}
                    </Token>
                  )}
                  {matchingVocab && (
                    <Token
                      className="bg-[rgb(143,231,138)]"
                      onClick={(event) =>
                        onVocabCheck(event, word, vocab)
                      }
                      key={`${word.token}-${index}`}
                    >
                      {word.token}
                    </Token>
                  )}
                </>
              )
            })}
          </p>
        )}
      </div>

      <Controls
        numVocab={numVocab}
        lang={lang}
        fromLang={fromLang}
        toLang={toLang}
        onFlip={onFlip}
        onOptions={onOptions}
        onVocab={onVocab}
        flipDisabled={!translation}
      />
    </div>
  )
}

export default Page
